/**
 * POS Error Handler Utility
 * Maps API error codes to user-friendly messages
 */
import { getErrorMessage } from "./errorMessage";

/**
 * POS API Error Codes from the handover document
 */
export const POS_ERROR_CODES = {
  SEAT_NOT_FOUND: "SEAT_NOT_FOUND",
  SEAT_INACTIVE: "SEAT_INACTIVE",
  ORDER_NOT_FOUND: "ORDER_NOT_FOUND",
  LINE_NOT_FOUND: "LINE_NOT_FOUND",
  ITEM_UNAVAILABLE: "ITEM_UNAVAILABLE",
  MODIFIER_INVALID: "MODIFIER_INVALID",
  LINES_REQUIRED: "LINES_REQUIRED",
  DISCOUNT_INVALID: "DISCOUNT_INVALID",
  INVOICE_NOT_FOUND: "INVOICE_NOT_FOUND",
  INVOICE_LOCKED: "INVOICE_LOCKED",
  PAYMENT_NOT_FOUND: "PAYMENT_NOT_FOUND",
  PAYMENT_METHOD_REQUIRED: "PAYMENT_METHOD_REQUIRED",
  PAYMENT_METHOD_CHANGE_REASON_REQUIRED:
    "PAYMENT_METHOD_CHANGE_REASON_REQUIRED",
  PAYMENT_METHOD_CHANGE_NOT_ALLOWED: "PAYMENT_METHOD_CHANGE_NOT_ALLOWED",
  PAYMENT_METHOD_CHANGE_PROVIDER_LOCKED:
    "PAYMENT_METHOD_CHANGE_PROVIDER_LOCKED",
  PAYMENT_METHOD_CHANGE_LOCKED: "PAYMENT_METHOD_CHANGE_LOCKED",
  PAYMENT_AMOUNT_INVALID: "PAYMENT_AMOUNT_INVALID",
  SPLIT_ITEMS_REQUIRED: "SPLIT_ITEMS_REQUIRED",
  SPLIT_QTY_INVALID: "SPLIT_QTY_INVALID",
  SPLIT_AMOUNT_INVALID: "SPLIT_AMOUNT_INVALID",
  ORDER_LOCKED: "ORDER_LOCKED",
  CONFLICT_STALE_VERSION: "CONFLICT_STALE_VERSION",
  POS_TABLE_DINE_IN_DISABLED: "POS_TABLE_DINE_IN_DISABLED",
  POS_DINE_IN_DISABLED: "POS_DINE_IN_DISABLED",
  POS_TAKEAWAY_DISABLED: "POS_TAKEAWAY_DISABLED",
  SEAT_ALREADY_OPENED: "SEAT_ALREADY_OPENED",
  CUSTOMER_DETAILS_REQUIRED: "CUSTOMER_DETAILS_REQUIRED",
  ITEM_STATION_REQUIRED: "ITEM_STATION_REQUIRED",
  ORDERING_BLOCKED: "ORDERING_BLOCKED",
};

/**
 * Default error messages for POS operations
 */
const ERROR_MESSAGES = {
  [POS_ERROR_CODES.SEAT_NOT_FOUND]:
    "Seat not found. Please refresh and try again.",
  [POS_ERROR_CODES.SEAT_INACTIVE]: "This seat is currently inactive.",
  [POS_ERROR_CODES.ORDER_NOT_FOUND]:
    "Order not found. Returning to seat board...",
  [POS_ERROR_CODES.LINE_NOT_FOUND]: "Order item not found. Please refresh.",
  [POS_ERROR_CODES.ITEM_UNAVAILABLE]: "This item is currently unavailable.",
  [POS_ERROR_CODES.MODIFIER_INVALID]: "Invalid modifier selection.",
  [POS_ERROR_CODES.LINES_REQUIRED]:
    "Cannot submit an empty order. Please add items first.",
  [POS_ERROR_CODES.DISCOUNT_INVALID]:
    "Invalid discount. Please check the amount or code.",
  [POS_ERROR_CODES.INVOICE_NOT_FOUND]: "Invoice not found. Please refresh.",
  [POS_ERROR_CODES.INVOICE_LOCKED]:
    "Cannot split this invoice because it is paid or has captured payment.",
  [POS_ERROR_CODES.PAYMENT_NOT_FOUND]:
    "Payment not found. Please refresh and try again.",
  [POS_ERROR_CODES.PAYMENT_METHOD_REQUIRED]: "Please select a payment method.",
  [POS_ERROR_CODES.PAYMENT_METHOD_CHANGE_REASON_REQUIRED]:
    "Please add a reason for the payment method change.",
  [POS_ERROR_CODES.PAYMENT_METHOD_CHANGE_NOT_ALLOWED]:
    "This payment method cannot be changed right now.",
  [POS_ERROR_CODES.PAYMENT_METHOD_CHANGE_PROVIDER_LOCKED]:
    "This payment is tied to an external provider and cannot be changed from POS.",
  [POS_ERROR_CODES.PAYMENT_METHOD_CHANGE_LOCKED]:
    "This order already has a refund, void, or return-void activity.",
  [POS_ERROR_CODES.PAYMENT_AMOUNT_INVALID]: "Invalid payment amount.",
  [POS_ERROR_CODES.SPLIT_ITEMS_REQUIRED]:
    "Select at least one item to split.",
  [POS_ERROR_CODES.SPLIT_QTY_INVALID]:
    "Selected quantity exceeds what is available in source invoice.",
  [POS_ERROR_CODES.SPLIT_AMOUNT_INVALID]:
    "Calculated split amount is invalid. Please refresh and try again.",
  [POS_ERROR_CODES.ORDER_LOCKED]:
    "This order is being modified by another user. Please try again.",
  [POS_ERROR_CODES.CONFLICT_STALE_VERSION]:
    "Order changed on another till. Review and submit again.",
  [POS_ERROR_CODES.POS_TABLE_DINE_IN_DISABLED]:
    "Table dine-in is disabled for this store.",
  [POS_ERROR_CODES.POS_DINE_IN_DISABLED]: "Dine-in is disabled for this store.",
  [POS_ERROR_CODES.POS_TAKEAWAY_DISABLED]:
    "Takeaway is disabled for this store.",
  [POS_ERROR_CODES.SEAT_ALREADY_OPENED]:
    "This table is already opened on another TILL.",
  [POS_ERROR_CODES.CUSTOMER_DETAILS_REQUIRED]:
    "Customer name, phone, and email are required for this order.",
  [POS_ERROR_CODES.ITEM_STATION_REQUIRED]:
    "Assign station first. This item cannot be ordered until a station is assigned while station mode is enabled.",
  [POS_ERROR_CODES.ORDERING_BLOCKED]:
    "Ordering is temporarily unavailable for this store.",
};

export const ITEM_STATION_REQUIRED_MESSAGE =
  "Assign station first. This item cannot be ordered until a station is assigned while station mode is enabled.";

export const ORDERING_BLOCKED_MESSAGE =
  "Ordering is temporarily unavailable for this store.";

export const isOrderingBlockedError = (error) => {
  const titleOrCode =
    error?.data?.title || error?.data?.code || error?.data?.errorCode;
  return (
    String(titleOrCode || "").trim().toUpperCase() ===
    POS_ERROR_CODES.ORDERING_BLOCKED
  );
};

export const isItemStationRequiredError = (error) => {
  const titleOrCode =
    error?.data?.title || error?.data?.code || error?.data?.errorCode;
  return (
    error?.status === 409 &&
    String(titleOrCode || "").trim().toUpperCase() ===
      POS_ERROR_CODES.ITEM_STATION_REQUIRED
  );
};

/**
 * Get user-friendly error message from API error
 * @param {Object} error - RTK Query error object
 * @returns {string} User-friendly error message
 */
export const getPosErrorMessage = (error) => {
  const detailMessage =
    typeof error?.data?.detail === "string" ? error.data.detail.trim() : "";
  const apiMessage =
    typeof error?.data?.message === "string" ? error.data.message.trim() : "";

  // ProblemDetails title support (some APIs use title instead of code)
  const problemTitle = error?.data?.title;
  if (isOrderingBlockedError(error)) {
    return ORDERING_BLOCKED_MESSAGE;
  }
  if (problemTitle === "ITEM_NOT_ALLOWED_FOR_ORDER_TYPE") {
    return (
      detailMessage ||
      apiMessage ||
      "This item is not available for this order type."
    );
  }
  if (problemTitle === POS_ERROR_CODES.ITEM_STATION_REQUIRED) {
    return ITEM_STATION_REQUIRED_MESSAGE;
  }
  if (problemTitle === POS_ERROR_CODES.PAYMENT_AMOUNT_INVALID) {
    return (
      detailMessage ||
      apiMessage ||
      "The entered amount is greater than the remaining balance. Please refresh the order and try again."
    );
  }

  // Check for error code in response
  const errorCode = error?.data?.code || error?.data?.errorCode;

  if (errorCode === POS_ERROR_CODES.SEAT_ALREADY_OPENED) {
    return (
      detailMessage ||
      apiMessage ||
      ERROR_MESSAGES[POS_ERROR_CODES.SEAT_ALREADY_OPENED]
    );
  }

  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // Check for detail message from API
  if (detailMessage) {
    return detailMessage;
  }

  // Check for message from API
  if (apiMessage) {
    return apiMessage;
  }

  // HTTP status-based messages
  if (error?.status === 404) {
    return "Resource not found. Please refresh and try again.";
  }

  if (error?.status === 409) {
    return "Conflict detected. Please refresh and try again.";
  }

  if (error?.status === 400) {
    return "Invalid request. Please check your input.";
  }

  if (error?.status === 401) {
    return "Session expired. Please log in again.";
  }

  if (error?.status === 403) {
    return "You don't have permission to perform this action.";
  }

  if (error?.status === 500) {
    return "Server error. Please try again later.";
  }

  // Network errors
  if (error?.status === "FETCH_ERROR") {
    return "Network error. Please check your connection.";
  }

  if (error?.status === "PARSING_ERROR") {
    return "Error processing server response.";
  }

  if (error?.status === "TIMEOUT_ERROR") {
    return "Request timed out. Please try again.";
  }

  // Default fallback
  return getErrorMessage(error, "An unexpected error occurred. Please try again.");
};

/**
 * Check if error requires navigation back to seat board
 * @param {Object} error - RTK Query error object
 * @returns {boolean}
 */
export const shouldNavigateToSeatBoard = (error) => {
  const errorCode = error?.data?.code || error?.data?.errorCode;

  // Navigate back on 404 errors for stale state
  return (
    errorCode === POS_ERROR_CODES.ORDER_NOT_FOUND ||
    errorCode === POS_ERROR_CODES.SEAT_NOT_FOUND ||
    error?.status === 404
  );
};

/**
 * Check if error is a validation error (user can fix)
 * @param {Object} error - RTK Query error object
 * @returns {boolean}
 */
export const isValidationError = (error) => {
  const errorCode = error?.data?.code || error?.data?.errorCode;

  return (
    errorCode === POS_ERROR_CODES.LINES_REQUIRED ||
    errorCode === POS_ERROR_CODES.DISCOUNT_INVALID ||
    errorCode === POS_ERROR_CODES.PAYMENT_METHOD_REQUIRED ||
    errorCode === POS_ERROR_CODES.PAYMENT_AMOUNT_INVALID ||
    errorCode === POS_ERROR_CODES.MODIFIER_INVALID ||
    error?.status === 400
  );
};

/**
 * Check if error is a conflict error (concurrent modification)
 * @param {Object} error - RTK Query error object
 * @returns {boolean}
 */
export const isConflictError = (error) => {
  const errorCode = error?.data?.code || error?.data?.errorCode;

  return (
    errorCode === POS_ERROR_CODES.ORDER_LOCKED ||
    errorCode === POS_ERROR_CODES.SEAT_INACTIVE ||
    errorCode === POS_ERROR_CODES.CONFLICT_STALE_VERSION ||
    errorCode === POS_ERROR_CODES.POS_TABLE_DINE_IN_DISABLED ||
    errorCode === POS_ERROR_CODES.POS_DINE_IN_DISABLED ||
    errorCode === POS_ERROR_CODES.POS_TAKEAWAY_DISABLED ||
    errorCode === POS_ERROR_CODES.ORDERING_BLOCKED ||
    isOrderingBlockedError(error) ||
    error?.status === 409
  );
};

/**
 * Format error for display in Alert component
 * @param {Object} error - RTK Query error object
 * @returns {Object} { message: string, tone: string }
 */
export const formatPosError = (error) => {
  const message = getPosErrorMessage(error);

  // Determine tone based on error type
  let tone = "error";

  if (isValidationError(error)) {
    tone = "warning";
  } else if (isConflictError(error)) {
    tone = "warning";
  }

  return { message, tone };
};

/**
 * Handle POS error with optional navigation callback
 * @param {Object} error - RTK Query error object
 * @param {Function} [onNavigateToSeatBoard] - Callback to navigate to seat board
 * @returns {string} Error message
 */
export const handlePosError = (error, onNavigateToSeatBoard) => {
  const message = getPosErrorMessage(error);

  // Navigate back to seat board if needed
  if (shouldNavigateToSeatBoard(error) && onNavigateToSeatBoard) {
    setTimeout(() => {
      onNavigateToSeatBoard();
    }, 2000); // Give user time to read the error
  }

  return message;
};
